import React, { useState } from 'react';
import { useAdminPageFetch, UsersByActivityData, UserEventActivityData } from '../../hooks/fetch/useAdminPageFetch';

const UserManagement: React.FC = () => {
    const { getUsersByActivity, getUsersEventActivity } = useAdminPageFetch();
    const [userData, setUserData] = useState<UsersByActivityData[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>('');
    const [downloadingCountryId, setDownloadingCountryId] = useState<string | null>(null);
    
    // Estados para los filtros de fecha
    const [dateFrom, setDateFrom] = useState<string>('');
    const [dateTo, setDateTo] = useState<string>('');

    // Establecer fechas por defecto (último mes)
    React.useEffect(() => {
        const today = new Date();
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
        
        setDateFrom(lastMonth.toISOString().split('T')[0]);
        setDateTo(today.toISOString().split('T')[0]);
    }, []);

    const handleSearch = async () => {
        if (!dateFrom || !dateTo) {
            setError('Por favor selecciona ambas fechas');
            return;
        }

        if (new Date(dateFrom) > new Date(dateTo)) {
            setError('La fecha inicial no puede ser mayor a la fecha final');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await getUsersByActivity({
                date_1: dateFrom,
                date_2: dateTo
            });

            if (response.success) {
                setUserData(response.data);
            } else {
                setError(response.message || 'Error al obtener los datos');
            }
        } catch (err) {
            setError('Error de conexión');
            console.error('Error fetching users by activity:', err);
        } finally {
            setLoading(false);
        }
    };

    const setQuickDateRange = (range: 'today' | 'week' | 'month' | 'quarter') => {
        const today = new Date();
        let fromDate = new Date();
        
        switch (range) {
            case 'today':
                fromDate = new Date(today);
                break;
            case 'week':
                fromDate = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
                break;
            case 'month':
                fromDate = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
                break;
            case 'quarter':
                fromDate = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
                break;
        }
        
        setDateFrom(fromDate.toISOString().split('T')[0]);
        setDateTo(today.toISOString().split('T')[0]);
    };

    const getTotalUsers = () => {
        return userData.reduce((total, country) => total + country.cantidad_de_usuarios_rango_fechas, 0);
    };

    const downloadExcel = async (countryId: string, countryName: string) => {
        if (!dateFrom || !dateTo) {
            setError('Por favor selecciona ambas fechas antes de descargar');
            return;
        }

        setDownloadingCountryId(countryId);
        setError('');

        try {
            const response = await getUsersEventActivity({
                date_1: dateFrom,
                date_2: dateTo,
                country_id: parseInt(countryId)
            });

            if (response.success && response.data) {
                // Crear contenido CSV compatible con Excel
                const headers = ['ID', 'Nombre', 'Email', 'Teléfono', 'Ciudad', 'Cantidad de Usos', 'Último Uso'];
                const csvContent = [
                    headers.join(','),
                    ...response.data.map((user: UserEventActivityData) => [
                        user.id,
                        `"${user.name.replace(/"/g, '""')}"`, // Escapar comillas dobles
                        user.email,
                        `"${user.phone.replace(/"/g, '""')}"`,
                        `"${user.city.replace(/"/g, '""')}"`,
                        user.cantidad_de_usos,
                        `"${new Date(user.ultimo_uso).toLocaleString('es-ES')}"`
                    ].join(','))
                ].join('\n');

                // Crear Blob con BOM para Excel
                const BOM = '\uFEFF';
                const content = new Blob([BOM + csvContent], { 
                    type: 'text/csv;charset=utf-8' 
                });

                // Crear enlace de descarga
                const encodedUri = window.URL.createObjectURL(content);
                const link = document.createElement("a");
                const date = new Date().toISOString().split('T')[0];
                const fileName = `Actividad-Usuarios-${countryName.replace(/\s+/g, '-')}-${date}.csv`;
                
                link.setAttribute("href", encodedUri);
                link.setAttribute("download", fileName);
                link.click();

                // Limpiar URL temporal
                window.URL.revokeObjectURL(encodedUri);
            } else {
                setError(response.message || 'Error al obtener los datos para descarga');
            }
        } catch (err) {
            setError('Error de conexión al descargar datos');
            console.error('Error downloading user event activity:', err);
        } finally {
            setDownloadingCountryId(null);
        }
    };

    return (
        <div className="tw-space-y-6">
            {/* Header */}
            <div>
                <h2 className="tw-text-2xl tw-font-bold tw-text-primary tw-mb-2">
                    Gestión de Usuarios por Actividad
                </h2>
                <p className="tw-text-base-content tw-opacity-70">
                    Consulta la actividad de usuarios por país en un rango de fechas específico
                </p>
            </div>

            {/* Filtros de fecha */}
            <div className="tw-card tw-bg-base-100 tw-shadow-lg">
                <div className="tw-card-body">
                    <h3 className="tw-card-title tw-text-lg">Filtros de Búsqueda</h3>
                    
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4 tw-items-end">
                        <div className="tw-form-control">
                            <label className="tw-label">
                                <span className="tw-label-text">Fecha Inicial</span>
                            </label>
                            <input
                                type="date"
                                className="tw-input tw-input-bordered"
                                style={{ 
                                    pointerEvents: 'auto',
                                    cursor: 'pointer',
                                    backgroundColor: 'white',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.375rem',
                                    padding: '0.5rem 0.75rem'
                                }}
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                            />
                        </div>

                        <div className="tw-form-control">
                            <label className="tw-label">
                                <span className="tw-label-text">Fecha Final</span>
                            </label>
                            <input
                                type="date"
                                className="tw-input tw-input-bordered"
                                style={{ 
                                    pointerEvents: 'auto',
                                    cursor: 'pointer',
                                    backgroundColor: 'white',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.375rem',
                                    padding: '0.5rem 0.75rem'
                                }}
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                            />
                        </div>

                        <div className="tw-form-control">
                            <button
                                className={`tw-btn tw-btn-primary ${loading ? 'tw-loading' : ''}`}
                                onClick={handleSearch}
                                disabled={loading}
                            >
                                {loading ? 'Buscando...' : 'Buscar'}
                            </button>
                        </div>
                    </div>

                    {/* Botones de acceso rápido para fechas */}
                    <div className="tw-mt-4">
                        <div className="tw-flex tw-flex-wrap tw-gap-2 tw-items-center">
                            <span className="tw-text-sm tw-text-gray-600 tw-mr-2">Acceso rápido:</span>
                            <button
                                type="button"
                                className="tw-btn tw-btn-xs tw-btn-outline"
                                onClick={() => setQuickDateRange('today')}
                            >
                                Hoy
                            </button>
                            <button
                                type="button"
                                className="tw-btn tw-btn-xs tw-btn-outline"
                                onClick={() => setQuickDateRange('week')}
                            >
                                Última semana
                            </button>
                            <button
                                type="button"
                                className="tw-btn tw-btn-xs tw-btn-outline"
                                onClick={() => setQuickDateRange('month')}
                            >
                                Último mes
                            </button>
                            <button
                                type="button"
                                className="tw-btn tw-btn-xs tw-btn-outline"
                                onClick={() => setQuickDateRange('quarter')}
                            >
                                Último trimestre
                            </button>
                        </div>
                    </div>

                    {error && (
                        <div className="tw-alert tw-alert-error tw-mt-4">
                            <svg className="tw-w-6 tw-h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{error}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Resumen de resultados */}
            {userData.length > 0 && (
                <div className="tw-stats tw-shadow tw-w-full">
                    <div className="tw-stat">
                        <div className="tw-stat-figure tw-text-primary">
                            <svg className="tw-w-8 tw-h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                        </div>
                        <div className="tw-stat-title">Total de Usuarios</div>
                        <div className="tw-stat-value tw-text-primary">{getTotalUsers().toLocaleString()}</div>
                        <div className="tw-stat-desc">En el período seleccionado</div>
                    </div>

                    <div className="tw-stat">
                        <div className="tw-stat-figure tw-text-secondary">
                            <svg className="tw-w-8 tw-h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div className="tw-stat-title">Países</div>
                        <div className="tw-stat-value tw-text-secondary">{userData.length}</div>
                        <div className="tw-stat-desc">Con actividad registrada</div>
                    </div>

                    <div className="tw-stat">
                        <div className="tw-stat-title">Período</div>
                        <div className="tw-stat-value tw-text-sm">
                            {new Date(dateFrom).toLocaleDateString('es-ES')}
                        </div>
                        <div className="tw-stat-desc">
                            hasta {new Date(dateTo).toLocaleDateString('es-ES')}
                        </div>
                    </div>
                </div>
            )}

            {/* Tabla de resultados */}
            {userData.length > 0 && (
                <div className="tw-card tw-bg-base-100 tw-shadow-lg">
                    <div className="tw-card-body">
                        <h3 className="tw-card-title">Usuarios por País</h3>
                        
                        <div className="tw-overflow-x-auto">
                            <table className="tw-table tw-table-zebra tw-w-full">
                                <thead>
                                    <tr>
                                        <th>País</th>
                                        <th>ID País</th>
                                        <th>Cantidad de Usuarios</th>
                                        <th>Porcentaje del Total</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {userData
                                        .sort((a, b) => b.cantidad_de_usuarios_rango_fechas - a.cantidad_de_usuarios_rango_fechas)
                                        .map((country) => {
                                            const percentage = ((country.cantidad_de_usuarios_rango_fechas / getTotalUsers()) * 100).toFixed(1);
                                            return (
                                                <tr key={country.idpais}>
                                                    <td>
                                                        <div className="tw-flex tw-items-center tw-space-x-3">
                                                            <div className="tw-font-bold">{country.nombre_pais}</div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="tw-badge tw-badge-ghost tw-badge-sm">
                                                            {country.idpais}
                                                        </span>
                                                    </td>
                                                    <td className="tw-font-mono tw-font-bold">
                                                        {country.cantidad_de_usuarios_rango_fechas.toLocaleString()}
                                                    </td>
                                                    <td>
                                                        <div className="tw-flex tw-items-center tw-space-x-2">
                                                            <div className="tw-w-16 tw-bg-gray-200 tw-rounded-full tw-h-2">
                                                                <div 
                                                                    className="tw-bg-primary tw-h-2 tw-rounded-full" 
                                                                    style={{width: `${percentage}%`}}
                                                                ></div>
                                                            </div>
                                                            <span className="tw-text-sm">{percentage}%</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <button
                                                            className={`tw-btn tw-btn-sm tw-btn-success ${downloadingCountryId === country.idpais ? 'tw-loading' : ''}`}
                                                            onClick={() => downloadExcel(country.idpais, country.nombre_pais)}
                                                            disabled={downloadingCountryId === country.idpais}
                                                            title={`Descargar detalle de actividad de usuarios de ${country.nombre_pais}`}
                                                        >
                                                            {downloadingCountryId === country.idpais ? (
                                                                'Descargando...'
                                                            ) : (
                                                                <>
                                                                    <svg className="tw-w-4 tw-h-4 tw-mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                                    </svg>
                                                                    Excel
                                                                </>
                                                            )}
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Estado vacío */}
            {userData.length === 0 && !loading && (
                <div className="tw-card tw-bg-base-100 tw-shadow-lg">
                    <div className="tw-card-body tw-text-center">
                        <svg className="tw-w-16 tw-h-16 tw-mx-auto tw-text-gray-400 tw-mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <h3 className="tw-text-lg tw-font-semibold tw-mb-2">No hay datos disponibles</h3>
                        <p className="tw-text-base-content tw-opacity-70">
                            Selecciona un rango de fechas y haz clic en "Buscar" para ver la actividad de usuarios.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagement;

